import { number, object, string } from "yup";

import ICustomer from "@/interfaces/customer.interfaces";
import { determineDocumentType } from "@/helpers/document-helpers";
import { AccountBalance, OperationType, PaymentPayload } from "@/interfaces/talk2pay.iterfaces";
import { PaymentFormMessages } from "@/messages/platforms/talk2PayHome/payment-form.messages";
import { DocumentType } from "@/types/document.types";

export const buildPaymentInitialData = (customer: ICustomer | null): PaymentPayload => {
  const federalTaxId = customer?.federalTaxId || null;
  const documentType = federalTaxId ? determineDocumentType(federalTaxId) : DocumentType.UNDEFINED;
  const pixAddressKeyType = documentType === DocumentType.CPF || documentType === DocumentType.CNPJ 
    ? documentType 
    : DocumentType.CNPJ;

  return {
    platformId: 0,
    beneficiaryAccountId: null,
    operationType: OperationType.PIX,
    pixAddressKey: federalTaxId,
    pixAddressKeyType: pixAddressKeyType,
    description: null,
    scheduleDate: null,
    amount: "",
  };
};

export const buildPaymentFormSchema = (
  messages: PaymentFormMessages,
  balance: AccountBalance | null
) => {
  return object().shape({
    operationType: string()
      .required(messages.errorRequiredOperationType)
      .oneOf([OperationType.TED, OperationType.PIX], messages.errorRequiredOperationType),

    beneficiaryAccountId: number()
      .nullable()
      .when("operationType", (value, schema) => {
        const operationType = value as unknown as OperationType;

        if (operationType[0] === OperationType.TED) {
          return schema.required(messages.errorRequiredBeneficiaryAccount);
        }
        return schema.nullable();
      }),

    amount: string()
      .required(messages.errorRequiredAmount)
      .matches(/^\d+(\.\d{1,2})?$/, messages.errorRequiredAmount)
      .test("isGreaterThanZero", messages.errorAmountMustBeGreaterThanZero, (value) => {
        if (!value) return false;
        const numericValue = parseFloat(value);
        return numericValue > 0;
      })
      .test("isLessOrEqualToBalance", messages.errorAmountOverBalance, (value) => {
        if (!value) return false;

        const numericValue = parseFloat(value);

        if (!balance || typeof balance.balance !== "number") return true;

        return numericValue <= balance.balance;
      }),

    pixAddressKey: string()
      .nullable()
      .when("operationType", {
        is: OperationType.PIX,
        then: (schema) => schema.required(messages.errorRequiredPixAddressKey),
        otherwise: (schema) => schema.nullable(),
      }),

    pixAddressKeyType: string()
      .nullable()
      .when("operationType", {
        is: OperationType.PIX,
        then: (schema) => schema.required(messages.errorRequiredPixAddressKeyType),
        otherwise: (schema) => schema.nullable(),
      }),
  });
};
